import { ReactNode } from 'react';
import { Box } from '@mantine/core';
import { COLORS } from '../../../src/constants/theme';
import { themeVar } from '../../../src/utils/pageTheme';

export interface ShowSidebarProps {
  children: ReactNode;
}

export interface ShowSidebarSectionProps {
  /** Uppercase section label (e.g., "Connect", "Support") */
  label?: string;
  /** Optional inline content rendered to the right of the label
      (e.g., an info icon / popover trigger). */
  labelAction?: ReactNode;
  children: ReactNode;
}

/**
 * Sidebar shell for show pages. Provides a sticky-scrolling left column
 * with panel background and separator. Slots for arbitrary sections
 * via children (typically <ShowSidebar.Section> subcomponents).
 *
 * Responsive: on <768px it becomes non-sticky and stacks above main content.
 * Consumers should conditionally hide primary CTAs at that breakpoint
 * and rely on <MobileCTABar> instead.
 */
function ShowSidebar({ children }: ShowSidebarProps) {
  return (
    <Box className="show-sidebar" component="aside">
      <style>{`
        .show-sidebar {
          /* A themed page can let its background through (#2706). */
          background: var(--show-sidebar-bg, ${COLORS.pageBgAlt});
          border-right: 1px solid ${COLORS.border};
          padding: 32px 24px;
          display: flex;
          flex-direction: column;
          gap: 32px;
          position: sticky;
          top: 0;
          max-height: 100vh;
          overflow-y: auto;
        }
        @media (max-width: 1199px) {
          .show-sidebar { padding: 24px 20px; gap: 24px; }
        }
        @media (max-width: 767px) {
          .show-sidebar {
            position: static;
            max-height: none;
            padding: 24px 20px;
            gap: 24px;
            border-right: none;
            border-bottom: 1px solid ${COLORS.border};
          }
        }
        .show-sidebar-label {
          font-family: 'Rubik', sans-serif;
          font-weight: 700;
          font-size: 14px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          /* The brand theme's text accent on a themed page; orange elsewhere. */
          color: ${themeVar('accent-text')};
          margin-bottom: 14px;
        }
        .show-sidebar-label-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
        }
        .show-sidebar-label-row .show-sidebar-label { margin-bottom: 0; }
      `}</style>
      {children}
    </Box>
  );
}

function ShowSidebarSection({ label, labelAction, children }: ShowSidebarSectionProps) {
  return (
    <div>
      {label && (
        labelAction ? (
          <div className="show-sidebar-label-row">
            <div className="show-sidebar-label">— {label}</div>
            {labelAction}
          </div>
        ) : (
          <div className="show-sidebar-label">— {label}</div>
        )
      )}
      {children}
    </div>
  );
}

ShowSidebar.Section = ShowSidebarSection;

export default ShowSidebar;
